import type { Database } from '@areapif/areapif-shared'

type InspectionPhoto = Database['public']['CompositeTypes']['inspection_photo']
type InspectionSeal = Database['public']['CompositeTypes']['inspection_seal']
type PhotoCategory = Database['public']['Enums']['photo_category']
type SealCategory = Database['public']['Enums']['seal_category']

export interface SealMetadata {
  serial_id: string
  owner: string
}

export const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20 MB

export const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/tiff']

/**
 * Validates that a file does not exceed the maximum allowed size and has a
 * supported MIME type. Throws a descriptive Error if either check fails.
 */
export const validateFile = (file: File): void => {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File "${file.name}" exceeds maximum size of 20 MB`)
  }
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    throw new Error(`File "${file.name}" is not a supported image type`)
  }
}

/**
 * Generates a unique storage name for a file by prefixing a UUID while
 * preserving the original file extension.
 */
export const generateStorageName = (originalName: string): string => {
  const dotIndex = originalName.lastIndexOf('.')
  const ext = dotIndex !== -1 ? originalName.slice(dotIndex) : ''
  return `${crypto.randomUUID()}${ext}`
}

/**
 * Builds an InspectionPhoto composite type value from a storage path and
 * photo category.
 */
export const buildInspectionPhoto = (path: string, category: PhotoCategory): InspectionPhoto => ({
  path,
  category,
})

/**
 * Builds an InspectionSeal composite type value from a storage path, seal
 * category, and seal metadata. The ocr_payload and cropped_image_url fields
 * are set to null as they are populated by a downstream process.
 */
export const buildInspectionSeal = (path: string, category: SealCategory, meta: SealMetadata): InspectionSeal => ({
  path,
  category,
  serial_id: meta.serial_id,
  owner: meta.owner,
  ocr_payload: null,
  cropped_image_url: null,
})

/**
 * Returns a new array that is the concatenation of the existing and incoming
 * photo arrays, without mutating either argument.
 */
export const mergePhotos = (existing: InspectionPhoto[], incoming: InspectionPhoto[]): InspectionPhoto[] => [...existing, ...incoming]

/**
 * Returns a new array that is the concatenation of the existing and incoming
 * seal arrays, without mutating either argument.
 */
export const mergeSeals = (existing: InspectionSeal[], incoming: InspectionSeal[]): InspectionSeal[] => [...existing, ...incoming]
